/* eslint-disable no-console */
/**
 * Lightweight debug logger for tracing the app load path (added to diagnose the Chrome-only
 * "tool not loading" issue). Every line is tagged and timestamped so the browser console shows the
 * exact order and timing of the boot / render / data-fetch sequence.
 *
 * All logging is centralised here so it is easy to find and remove, and so `no-console` is disabled
 * in ONE place rather than scattered across the app. Logging is on by default while debugging; set
 * `window.__DMI_DEBUG__ = false` in the console (or localStorage `dmi_debug=off`) to silence it.
 */

function isEnabled() {
  if (typeof window === 'undefined') return false;
  if (window.__DMI_DEBUG__ === false) return false;
  try {
    if (window.localStorage?.getItem('dmi_debug') === 'off') return false;
  } catch {
    // localStorage may be unavailable (privacy mode) — fall through to enabled.
  }
  return true;
}

function timestamp() {
  // performance.now() is monotonic and shows elapsed ms since page load — ideal for ordering.
  const elapsed = typeof performance !== 'undefined' ? Math.round(performance.now()) : 0;
  return `+${elapsed}ms`;
}

/** Info-level trace: debugLog('ShortCampaign', 'getAll start', { rowCount }). */
export function debugLog(tag, message, ...details) {
  if (!isEnabled()) return;
  console.log(`[DMI ${timestamp()}] [${tag}] ${message}`, ...details);
}

/** Error-level trace; always logged (not gated) so failures are never hidden. */
export function debugError(tag, message, ...details) {
  console.error(`[DMI ${timestamp()}] [${tag}] ${message}`, ...details);
}

let globalHandlersInstalled = false;

/**
 * Installs window-level handlers so uncaught errors and unhandled promise rejections are logged
 * with a clear tag before they blank the page. Safe to call more than once.
 */
export function installGlobalErrorLogging() {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    debugError('window.onerror', event.message, {
      source: event.filename,
      line: event.lineno,
      col: event.colno,
      error: event.error,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    debugError('window.unhandledrejection', 'Unhandled promise rejection', event.reason);
  });

  debugLog('bootstrap', 'global error logging installed', {
    userAgent: window.navigator?.userAgent,
    language: window.navigator?.language,
    url: window.location?.href,
  });
}
